// @prod-safe — passive image integrity scanner. Renders null.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Runtime safety net that scans every <img> on the page after a route change
 * and reports broken or zero-pixel assets. Targets hero and About / AI Training
 * imagery in particular but runs site-wide.
 *
 * Flags:
 *   - <img> with no src / empty src
 *   - <img> that finished loading with naturalWidth === 0 or naturalHeight === 0
 *   - <img> that fired an `error` event (decoded as broken)
 *
 * DEV: console.error + red outline. PROD: single console.warn (silent UI).
 */

const SCOPE_HINTS = [
  "hero",
  "about",
  "ai-training",
  "audience",
  "split-",
];

interface ImgHit {
  reason: string;
  src: string;
  scope: string;
  element: HTMLImageElement;
}

const describeScope = (img: HTMLImageElement): string => {
  const probe = `${img.src} ${img.alt} ${img.className} ${img.closest("section,header,div")?.id ?? ""}`.toLowerCase();
  for (const hint of SCOPE_HINTS) {
    if (probe.includes(hint)) return hint;
  }
  return "generic";
};

const isHidden = (img: HTMLImageElement): boolean => {
  const style = window.getComputedStyle(img);
  if (style.display === "none" || style.visibility === "hidden") return true;
  // Off-screen lazy images that have not started loading should not be flagged.
  if (img.loading === "lazy" && !img.complete) return true;
  return false;
};

const inspect = (img: HTMLImageElement): ImgHit | null => {
  if (isHidden(img)) return null;
  const src = img.currentSrc || img.src;
  if (!src || src.endsWith("#") || src === window.location.href) {
    return { reason: "Missing src", src: src || "(empty)", scope: describeScope(img), element: img };
  }
  if (img.complete) {
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      return {
        reason: `Zero-pixel asset (naturalWidth=${img.naturalWidth}, naturalHeight=${img.naturalHeight})`,
        src,
        scope: describeScope(img),
        element: img,
      };
    }
  }
  return null;
};

const report = (hits: ImgHit[]) => {
  if (hits.length === 0) return;
  const isDev = Boolean(import.meta.env?.DEV) && !import.meta.env?.PROD;
  const header = `[broken-image-assertion] Found ${hits.length} broken image${hits.length === 1 ? "" : "s"}`;

  if (isDev) {
    // eslint-disable-next-line no-console
    console.group(`%c${header}`, "color:#fff;background:#b91c1c;padding:2px 6px;border-radius:3px;font-weight:600;");
    hits.forEach((h, i) => {
      // eslint-disable-next-line no-console
      console.error(`#${i + 1} [${h.scope}] ${h.reason} → ${h.src}`, h.element);
      h.element.setAttribute("data-broken-image", "true");
      h.element.style.outline = "3px dashed #b91c1c";
      h.element.style.outlineOffset = "2px";
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  } else {
    // eslint-disable-next-line no-console
    console.warn(header, hits.map((h) => ({ scope: h.scope, reason: h.reason, src: h.src })));
  }
};

const scan = (): ImgHit[] => {
  const hits: ImgHit[] = [];
  document.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
    const hit = inspect(img);
    if (hit) hits.push(hit);
  });
  return hits;
};

const BrokenImageAssertion = () => {
  const location = useLocation();

  useEffect(() => {
    const errorHandler = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "IMG") return;
      const img = target as HTMLImageElement;
      if (isHidden(img)) return;
      report([{
        reason: "Image failed to load (error event)",
        src: img.currentSrc || img.src || "(empty)",
        scope: describeScope(img),
        element: img,
      }]);
    };
    // Capture phase — `error` does not bubble.
    window.addEventListener("error", errorHandler, true);

    const tick = (delay: number) =>
      window.setTimeout(() => {
        try {
          report(scan());
        } catch {
          /* never break the app */
        }
      }, delay);

    // Two passes: shortly after mount, and once below-the-fold lazy images
    // have likely had a chance to resolve.
    const ids = [tick(150), tick(2500)];

    return () => {
      window.removeEventListener("error", errorHandler, true);
      ids.forEach((id) => window.clearTimeout(id));
    };
  }, [location.pathname]);

  return null;
};

export default BrokenImageAssertion;
